import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  SymbolListsRegistryService,
  SymbolListRegistryDoc,
  HistoryCommit,
} from '../../../@services/symbol-lists-registry.service';
import { ToastService } from '../../../@services/toast.service';
import { CopySourceButtonComponent } from '../../../@shared/copy-source-button/copy-source-button.component';

// Symbol Lists Registry pane — CRUD on F2-ADMIN.SymbolLists docs that
// point at GitHub repo/branch/path/format/ticker_column for each list.
// Editing a doc is the code-free way to add a new list, swap the repo
// behind an existing name, or pin the ticker column on a nonstandard
// csv header.
//
// FLEET FORMS RULE (feedback_forms_save_revert_cancel_no_autosave):
// every editable field routes through the form's dirty tracker; Save
// commits all pending fields in one PUT; Revert restores the last-loaded
// snapshot; Cancel exits edit mode with an unsaved-close confirm.
//
// Related:
//   - CommonSymbolListsService — content edits (still commit to GitHub)
//   - runbook: F2 symbol-lists REGISTRY-in-Mongo (IT-F2-43 corrected)
@Component({
  selector: 'app-symbol-lists-registry',
  standalone: true,
  imports: [CommonModule, FormsModule, CopySourceButtonComponent],
  templateUrl: './symbol-lists-registry.component.html',
  host: { class: 'block h-full' },
})
export class SymbolListsRegistryComponent implements OnInit {
  // Optional pre-selected list name — set by the hub when the operator
  // clicks the gear on a GitHub Symbol Lists row. Component picks up
  // this row on init instead of landing on the idle prompt.
  @Input() preselectName: string | null = null;

  entries: SymbolListRegistryDoc[] = [];
  loading = false;

  // Dropdown options — populated from GitHub-lookup endpoints as the
  // operator edits the form. Nice-to-have on top of the raw text input;
  // failures leave the text input working unchanged.
  branchOptions: string[] = [];
  pathOptions: string[] = [];
  pathShowAll = false;
  private _branchLookupKey = '';
  private _pathLookupKey = '';

  mode: 'idle' | 'view' | 'new' = 'idle';
  selectedName = '';

  // Form fields. `original` holds the last-loaded snapshot for revert.
  form: {
    name: string;
    label: string;
    repo: string;
    branch: string;
    path: string;
    format: 'txt' | 'csv';
    ticker_column: string;
    customer_slug: string;
    scanner_slug: string;
    active: boolean;
    // IT-F2-230: per-list role gates. reader_role controls VIEW in the
    // client app's Common Data section; writer_role controls EDIT.
    // Defaults 'admin' — admin explicitly opts a list into 'client'.
    reader_role: 'admin' | 'client';
    writer_role: 'admin' | 'client';
  } = this._emptyForm();
  original: typeof this.form = this._emptyForm();
  saving = false;
  deleting = false;
  // IT-F2-230 Sub 4: git commit history state. Lazy-loaded on demand
  // so opening a registry row doesn't burn a GitHub API call every time
  // (rate limit + latency both matter).
  history: HistoryCommit[] = [];
  historyLoading = false;
  historyLoadedFor: string | null = null;    // last name we loaded — invalidate on selectEntry
  historyExpanded = false;

  constructor(
    private svc: SymbolListsRegistryService,
    private toast: ToastService,
  ) {}

  async ngOnInit() {
    await this.reload();
    if (this.preselectName) {
      const nm = this.preselectName;
      const found = this.entries.find((e) => e._id === nm);
      if (found) {
        await this.selectEntry(nm);
      } else {
        // Preselected name doesn't have a registry doc yet — drop the
        // operator straight into the New-entry form with the name
        // prefilled so they can add the pointer they came here for.
        this.startNew();
        this.form.name = nm;
      }
    }
  }

  async reload() {
    this.loading = true;
    try {
      this.entries = await this.svc.listAll();
    } finally {
      this.loading = false;
    }
  }

  async selectEntry(name: string) {
    if (this.dirty && !confirm(`Discard unsaved changes to "${this.form.name || 'draft'}"?`)) return;
    const doc = await this.svc.getOne(name);
    if (!doc) {
      this.toast.error('Load failed', `Could not load registry '${name}' — it may have been deleted.`);
      await this.reload();
      return;
    }
    this._loadFormFromDoc(doc);
    this.mode = 'view';
    this.selectedName = doc._id;
    // Kick off dropdown lookups for the loaded repo — non-blocking.
    void this._refreshBranchOptions();
    void this._refreshPathOptions();
    // IT-F2-230 Sub 4: invalidate any cached history from a prior
    // selection. Auto-collapse the panel so switching entries doesn't
    // stream stale commits in.
    this.history = [];
    this.historyLoadedFor = null;
    this.historyExpanded = false;
    this.historyLoading = false;
  }

  // IT-F2-230 Sub 4: lazy fetch of the git commit log for the currently
  // selected list. Toggle-open triggers the fetch on first expand; the
  // result is cached until a different list is selected (see selectEntry).
  async toggleHistory() {
    this.historyExpanded = !this.historyExpanded;
    if (!this.historyExpanded) return;
    if (this.historyLoadedFor === this.selectedName) return;
    if (!this.selectedName) return;
    this.historyLoading = true;
    try {
      this.history = await this.svc.listHistory(this.selectedName);
      this.historyLoadedFor = this.selectedName;
    } finally {
      this.historyLoading = false;
    }
  }

  async refreshHistory() {
    if (!this.selectedName) return;
    this.historyLoading = true;
    try {
      this.history = await this.svc.listHistory(this.selectedName);
      this.historyLoadedFor = this.selectedName;
    } finally {
      this.historyLoading = false;
    }
  }

  startNew() {
    if (this.dirty && !confirm('Discard unsaved changes?')) return;
    this.form = this._emptyForm();
    this.original = this._emptyForm();
    this.mode = 'new';
    this.selectedName = '';
    void this._refreshBranchOptions();
    void this._refreshPathOptions();
  }

  // Called from (change) on the repo/branch inputs so the datalists
  // stay in sync as the operator retypes. Debounced by lookup-key
  // dedupe (below) so a same-repo change doesn't refetch.
  onRepoChange() {
    void this._refreshBranchOptions();
    // Branch may have been cleared if the operator retyped repo before
    // touching branch — but we still want to try a path lookup with
    // whatever branch is currently set.
    void this._refreshPathOptions();
  }

  onBranchChange() {
    void this._refreshPathOptions();
  }

  // Copy the current form state as a normalized JSON payload — the same
  // shape POST /rest/symbol-lists/registry accepts. Two use cases: (1)
  // duplicate an entry (paste + change the name), (2) share a config
  // via chat / commit / gist. Empty strings are dropped so the JSON
  // stays tight.
  async copyJson() {
    const payload: Record<string, any> = {};
    const put = (k: string, v: any) => {
      if (v === '' || v === null || v === undefined) return;
      payload[k] = v;
    };
    put('name', this.form.name.trim().toLowerCase());
    put('label', this.form.label?.trim());
    put('repo', this.form.repo?.trim());
    put('branch', this.form.branch?.trim());
    put('path', this.form.path?.trim());
    put('format', this.form.format);
    put('ticker_column', this.form.ticker_column?.trim());
    put('customer_slug', this.form.customer_slug?.trim().toLowerCase());
    put('scanner_slug', this.form.scanner_slug?.trim().toLowerCase());
    // active is boolean — always meaningful
    payload['active'] = this.form.active;
    // IT-F2-230: role gates — always emit so a paste-then-save round-trip
    // is lossless (both fields have a meaningful default of 'admin').
    payload['reader_role'] = this.form.reader_role;
    payload['writer_role'] = this.form.writer_role;
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      this.toast.success('Copied to clipboard', `${text.length} chars`);
    } catch (e: any) {
      this.toast.error('Copy failed', e?.message || 'Clipboard not available in this context.');
    }
  }

  // Read JSON from clipboard, validate loosely, populate the form.
  // Behavior:
  //   - New mode: every field including name is populated.
  //   - View mode: name is IGNORED (immutable — the operator picked
  //     this row for a reason); everything else populates.
  //   - Unknown fields silently ignored.
  //   - Sets dirty so Save is armed but requires an explicit click.
  async pasteJson() {
    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch (e: any) {
      this.toast.error('Paste failed', e?.message || 'Clipboard not available. Try Ctrl+V into a text field or grant permission.');
      return;
    }
    if (!text?.trim()) {
      this.toast.error('Nothing on clipboard', 'Copy a JSON registry payload first, then Paste.');
      return;
    }
    let payload: any;
    try {
      payload = JSON.parse(text);
    } catch (e: any) {
      this.toast.error('Invalid JSON', e?.message || 'Clipboard text did not parse as JSON.');
      return;
    }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      this.toast.error('Invalid payload', 'Expected a JSON object with registry fields.');
      return;
    }
    // In new mode, allow name to come from the payload; in view mode
    // the name is immutable so we skip it.
    if (this.mode === 'new' && typeof payload.name === 'string') {
      this.form.name = String(payload.name).trim().toLowerCase();
    }
    if (typeof payload.label === 'string') this.form.label = String(payload.label);
    if (typeof payload.repo === 'string') this.form.repo = String(payload.repo).trim();
    if (typeof payload.branch === 'string') this.form.branch = String(payload.branch).trim();
    if (typeof payload.path === 'string') this.form.path = String(payload.path).trim();
    if (payload.format === 'csv' || payload.format === 'txt') this.form.format = payload.format;
    if (typeof payload.ticker_column === 'string') this.form.ticker_column = String(payload.ticker_column).trim();
    if (typeof payload.customer_slug === 'string') this.form.customer_slug = String(payload.customer_slug).trim().toLowerCase();
    if (typeof payload.scanner_slug === 'string') this.form.scanner_slug = String(payload.scanner_slug).trim().toLowerCase();
    if (typeof payload.active === 'boolean') this.form.active = payload.active;
    // IT-F2-230: role gates from paste — clamp to whitelist, ignore anything else.
    if (payload.reader_role === 'admin' || payload.reader_role === 'client') this.form.reader_role = payload.reader_role;
    if (payload.writer_role === 'admin' || payload.writer_role === 'client') this.form.writer_role = payload.writer_role;
    // Refresh lookup dropdowns for the potentially-new repo/branch.
    void this._refreshBranchOptions();
    void this._refreshPathOptions();
    this.toast.success('Pasted', 'Form populated from clipboard — review then Save.');
  }

  togglePathShowAll() {
    this.pathShowAll = !this.pathShowAll;
    this._pathLookupKey = '';   // force a refetch since the filter changed
    void this._refreshPathOptions();
  }

  private async _refreshBranchOptions() {
    const repo = (this.form.repo || '').trim();
    if (!repo || !/^[^/]+\/[^/]+$/.test(repo)) { this.branchOptions = []; return; }
    if (repo === this._branchLookupKey) return;
    this._branchLookupKey = repo;
    this.branchOptions = await this.svc.lookupBranches(repo);
  }

  private async _refreshPathOptions() {
    const repo = (this.form.repo || '').trim();
    const branch = (this.form.branch || 'main').trim() || 'main';
    if (!repo || !/^[^/]+\/[^/]+$/.test(repo)) { this.pathOptions = []; return; }
    const key = `${repo}@${branch}|${this.pathShowAll ? 'all' : 'lists'}`;
    if (key === this._pathLookupKey) return;
    this._pathLookupKey = key;
    this.pathOptions = await this.svc.lookupPaths(repo, branch, this.pathShowAll);
  }

  revert() {
    if (!this.dirty) return;
    if (!confirm('Revert unsaved changes back to the last-loaded values?')) return;
    this.form = { ...this.original };
  }

  cancelEdit() {
    if (this.dirty && !confirm('Discard unsaved changes?')) return;
    this.mode = 'idle';
    this.selectedName = '';
    this.form = this._emptyForm();
    this.original = this._emptyForm();
  }

  // Dirty = any form field diverges from the last-loaded snapshot.
  // Used to gate Save/Revert enablement + confirm on navigation-away.
  get dirty(): boolean {
    return (Object.keys(this.form) as (keyof typeof this.form)[])
      .some((k) => this.form[k] !== this.original[k]);
  }

  // Validation guard for Save. All rules mirror the backend so the SPA
  // rejects locally before firing the PUT.
  get canSave(): boolean {
    if (!this.dirty) return false;
    if (!this._nameValid(this.form.name)) return false;
    if (!this.form.repo.trim() || !/^[^/]+\/[^/]+$/.test(this.form.repo.trim())) return false;
    if (!this.form.path.trim()) return false;
    return true;
  }

  async save() {
    if (!this.canSave) return;
    const name = this.form.name.trim().toLowerCase();
    this.saving = true;
    try {
      const patch = {
        repo: this.form.repo.trim(),
        branch: (this.form.branch || 'main').trim(),
        path: this.form.path.trim(),
        format: this.form.format,
        ticker_column: this.form.ticker_column?.trim() || undefined,
        label: this.form.label?.trim() || undefined,
        active: this.form.active,
        // Empty string tells the backend to clear the scope (fleet-wide);
        // a non-empty value scopes to that slug. undefined would leave
        // the field alone on the backend, which we don't want since the
        // form always reflects the current state.
        customer_slug: this.form.customer_slug?.trim() || '',
        scanner_slug: this.form.scanner_slug?.trim() || '',
        // IT-F2-230: per-list role gates — always send both so the form
        // state is authoritative (matches the customer_slug pattern).
        reader_role: this.form.reader_role,
        writer_role: this.form.writer_role,
      };
      const res = this.mode === 'new'
        ? await this.svc.create({ name, ...patch, repo: patch.repo!, path: patch.path! })
        : await this.svc.update(name, patch);
      if (!res?.ok) return;
      this.toast.success(res.action === 'created' ? 'Registry entry created' : 'Saved',
        `${name}${res.refresh?.count_lists != null ? ` — ${res.refresh.count_lists} lists, ${res.refresh.count_symbols} symbols` : ''}`);
      // Re-anchor the form to the doc the server returned so dirty flips
      // false on a successful save (no phantom-dirty from server-derived
      // fields like updated_at).
      if (res.doc) this._loadFormFromDoc(res.doc);
      this.mode = 'view';
      this.selectedName = res.name;
      await this.reload();
    } finally {
      this.saving = false;
    }
  }

  async remove() {
    if (this.mode !== 'view' || !this.selectedName) return;
    if (!confirm(`Delete registry entry "${this.selectedName}"? The GitHub file is left in place — this only removes the pointer + Redis cache.`)) return;
    this.deleting = true;
    try {
      const ok = await this.svc.remove(this.selectedName);
      if (!ok) return;
      this.toast.success('Registry entry deleted', this.selectedName);
      this.mode = 'idle';
      this.selectedName = '';
      this.form = this._emptyForm();
      this.original = this._emptyForm();
      await this.reload();
    } finally {
      this.deleting = false;
    }
  }

  // Toggle handler for the sidebar Active dot — persists the flip in a
  // single PUT so the operator doesn't need to open the form to disable
  // a list. This is the FLEET FORMS RULE exception for single-click
  // atomic toggles.
  async toggleActiveInList(entry: SymbolListRegistryDoc) {
    const nextActive = !(entry.active !== false);
    const ok = await this.svc.update(entry._id, { active: nextActive });
    if (!ok) return;
    this.toast.success(nextActive ? 'Activated' : 'Deactivated', entry._id);
    await this.reload();
    if (this.selectedName === entry._id) {
      const doc = await this.svc.getOne(entry._id);
      if (doc) this._loadFormFromDoc(doc);
    }
  }

  formatBadgeClass(fmt?: string): string {
    if (fmt === 'csv') return 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-200';
    return 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200';
  }

  trackByName(_: number, e: SymbolListRegistryDoc) { return e._id; }

  private _emptyForm(): typeof this.form {
    return {
      name: '',
      label: '',
      repo: 'F2Tech-WebDev-v2/common-symbol-lists',   // sensible default so a new-list POST rarely needs this field touched
      branch: 'main',
      path: '',
      format: 'txt',
      ticker_column: '',
      customer_slug: '',   // empty = fleet-wide (backend nulls on empty)
      scanner_slug: '',
      active: true,
      reader_role: 'admin',   // IT-F2-230: safe default — admin explicitly opts in
      writer_role: 'admin',
    };
  }

  private _loadFormFromDoc(doc: SymbolListRegistryDoc) {
    this.form = {
      name: String(doc._id || ''),
      label: doc.label || '',
      repo: doc.repo || '',
      branch: doc.branch || 'main',
      path: doc.path || '',
      format: (doc.format === 'csv' ? 'csv' : 'txt') as 'txt' | 'csv',
      ticker_column: doc.ticker_column || '',
      customer_slug: doc.customer_slug || '',
      scanner_slug: doc.scanner_slug || '',
      active: doc.active !== false,
      reader_role: (doc.reader_role === 'client' ? 'client' : 'admin'),
      writer_role: (doc.writer_role === 'client' ? 'client' : 'admin'),
    };
    this.original = { ...this.form };
  }

  private _nameValid(name: string): boolean {
    return /^[a-z0-9][a-z0-9_-]{0,63}$/.test((name || '').trim().toLowerCase());
  }
}
